import { Box, Tabs } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { displayClasses } from 'styles';
import { CreditOptionsResponse, CreditResponse } from 'types';

import { CreditComparisonSource } from './request/comparison-source';
import CreditDocumentation from './documentation';
import CreditPayment from './payment';
import CreditRequest from './request';

const TABS = {
  REQUEST: 'request',
  DOCUMENTATION: 'documentation',
  PAYMENT: 'payment',
};

const PAYMENT_LOCKED_STATUSES = [
  'DRAFT',
  'NEW_REQUEST',
  'INCOMPLETE_DOCUMENTATION',
  'PROCESSING',
] as const;

interface CreditTabsProps {
  credit: CreditResponse;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  updateService?: (id: number, data: any) => Promise<void>;
  comparisonSource?: CreditComparisonSource | null;
}

const CreditTabs = ({
  credit,
  options,
  readOnly = false,
  updateService,
  comparisonSource,
}: CreditTabsProps) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Tabs defaultValue={TABS.REQUEST} keepMounted={false} variant={'pills'} mt={'lg'}>
        <Tabs.List>
          <Tabs.Tab value={TABS.REQUEST} className={displayClasses.tabList}>
            {t('credit.view.tab.' + TABS.REQUEST)}
          </Tabs.Tab>
          <Tabs.Tab
            value={TABS.DOCUMENTATION}
            disabled={!credit.requestedAt}
            className={displayClasses.tabList}
          >
            {t('credit.view.tab.' + TABS.DOCUMENTATION)}
          </Tabs.Tab>
          <Tabs.Tab
            value={TABS.PAYMENT}
            disabled={PAYMENT_LOCKED_STATUSES.includes(credit.status as typeof PAYMENT_LOCKED_STATUSES[number])}
            className={displayClasses.tabList}
          >
            {t('credit.view.tab.' + TABS.PAYMENT)}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={TABS.REQUEST} className={displayClasses.tabContent}>
          <CreditRequest
            credit={credit}
            options={options}
            readOnly={readOnly}
            updateService={updateService}
            comparisonSource={comparisonSource}
          />
        </Tabs.Panel>
        <Tabs.Panel value={TABS.DOCUMENTATION} className={displayClasses.tabContent}>
          <CreditDocumentation credit={credit} />
        </Tabs.Panel>
        <Tabs.Panel value={TABS.PAYMENT} className={displayClasses.tabContent}>
          <CreditPayment options={options} credit={credit} readOnly={readOnly} />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
};

export default CreditTabs;
